// frontend/src/components/payments/PaymentForm.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Grid,
} from "@mui/material";
import { AccountBalance, CreditCard, Security } from "@mui/icons-material";
import { paymentsAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const PaymentForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    amount: "",
    paymentType: "",
    description: "",
    roomId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);

  const paymentTypes = [
    { value: "monthly_rent", label: "Monthly Rent" },
    { value: "security_deposit", label: "Security Deposit" },
    { value: "maintenance", label: "Maintenance Fee" },
    { value: "fine", label: "Fine" },
    { value: "laundry", label: "Laundry" },
    { value: "mess_fee", label: "Mess Fee" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.paymentType) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await paymentsAPI.initiatePhonePePayment({
        ...formData,
        userId: user?.id || user?._id
      });
      
      if (response.data.success && response.data.url) {
        // Redirect to PhonePe
        window.location.href = response.data.url;
      } else {
        setError("Failed to generate payment URL");
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return "";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {/* Razorpay Header */}
      <Paper
        sx={{ p: 2, mb: 3, bgcolor: "#f8f9ff", border: "1px solid #e3e6ff" }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Security color="primary" />
          <Typography variant="h6" color="primary">
            Secure Payment Gateway
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Accepted Payment Methods: 💳 Cards • 🏦 Net Banking • 📱 UPI • 💰
          Wallets
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {processingPayment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            Processing payment... Please do not close this window.
          </Box>
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Payment Type</InputLabel>
            <Select
              name="paymentType"
              value={formData.paymentType}
              label="Payment Type"
              onChange={handleChange}
              disabled={loading || processingPayment}
            >
              {paymentTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="amount"
            label="Amount (₹)"
            type="number"
            value={formData.amount}
            onChange={handleChange}
            fullWidth
            required
            disabled={loading || processingPayment}
            inputProps={{ min: "1", step: "0.01" }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="description"
            label="Description (Optional)"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            disabled={loading || processingPayment}
          />
        </Grid>

        {formData.amount && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle1" gutterBottom>
                Payment Summary
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Amount:</Typography>
                <Typography>{formatAmount(formData.amount)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Processing Fee:</Typography>
                <Typography>₹0.00</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight="bold">
                  Total:
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formatAmount(formData.amount)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              bgcolor: "success.50",
              border: "1px solid",
              borderColor: "success.200",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Security color="success" />
              <Typography
                variant="subtitle2"
                color="success.main"
                fontWeight="bold"
              >
                🔒 Your payment is 100% secure
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              • 256-bit SSL encryption • PCI DSS Level 1 compliant • Trusted by
              8M+ businesses
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
        <Button onClick={onCancel} disabled={loading || processingPayment}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={
            loading ||
            processingPayment ||
            !formData.amount ||
            !formData.paymentType
          }
          startIcon={loading ? <CircularProgress size={20} /> : <CreditCard />}
          sx={{
            background: "linear-gradient(45deg, #528ff0 30%, #52a9ff 90%)",
            color: "white",
            fontWeight: "bold",
            fontSize: "1.1rem",
            py: 1.5,
            px: 3,
            "&:hover": {
              background: "linear-gradient(45deg, #3f7ee0 30%, #4298ef 90%)",
            },
          }}
        >
          {loading
            ? "Initiating..."
            : `Pay ${formatAmount(formData.amount) || "₹0"} via PhonePe`}
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentForm;
